/**
 * Hostile-input handling for drafts.
 *
 * A draft is not always typed by the person looking at the screen: an embed host supplies
 * prefills, an AI proposes one, an API caller POSTs one. All of those are untrusted, so the
 * package treats every draft as attacker-controlled and never as a promise about its contents.
 *
 * Two layers, deliberately separate:
 *
 * - **`sanitizeDraft`** — makes a draft *safe to render and emit*: strips the control
 *   characters that let a value break out of its line or field, and clamps sizes. Silent by
 *   design, because it runs on data the user never typed.
 * - **`validateEvent`** (validate.ts) — tells the *user* what's wrong with what they typed.
 *
 * Generators sanitize their own output too (see `ics.ts`), so a caller who skips this still
 * cannot inject a property. Defence in depth: this is the polite layer, not the only one.
 */

import type { EventDraft, EventPerson, EventRecurrence } from './model';

/**
 * Caps on user-supplied text. Generous enough that no honest event hits them, small enough
 * that a hostile draft can't turn one request into a multi-megabyte file.
 */
export const LIMITS = {
  title: 200,
  location: 500,
  description: 5_000,
  url: 2_000,
  personName: 100,
  email: 254, // RFC 5321 §4.5.3.1
  attendees: 100,
  reminders: 10,
  recurrenceCount: 1_000,
  /** "Every N weeks" — the ceiling the sanitizer clamps to, and what a form should offer. */
  recurrenceInterval: 1_000,
  /** Events in one multi-event export. */
  events: 1_000,
} as const;

/**
 * Strips characters that have structural meaning in the output formats or none at all in a
 * calendar field: C0 controls (except the newlines and tabs a description may legitimately
 * carry), DEL, and the C1 range. `\r` goes too — a lone CR is how a value tries to start its
 * own content line.
 */
// eslint-disable-next-line no-control-regex -- stripping control characters is the point
const CONTROL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\r]/g;

/** Text safe to place in a field: control characters removed, length clamped. */
export function sanitizeText(value: string, maxLength: number): string {
  return value.replace(CONTROL_CHARS, '').slice(0, maxLength);
}

/** Single-line text (titles, locations, zone names): newlines and tabs collapse to spaces. */
export function sanitizeLine(value: string, maxLength: number): string {
  return sanitizeText(value.replace(/[\n\t]+/g, ' '), maxLength);
}

/**
 * An email safe to emit in a `mailto:` — the address grammar we accept is narrow on purpose,
 * because an address is pasted straight into an iCalendar property value. Anything else
 * returns `null` and the person is dropped rather than half-written.
 */
export function sanitizeEmail(value: string): string | null {
  const email = value.trim();
  if (email.length > LIMITS.email) return null;
  return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email) ? email : null;
}

/** An `http(s)` URL, or `null`. Blocks `javascript:`/`data:` and anything with whitespace. */
export function sanitizeUrl(value: string): string | null {
  const url = value.trim();
  if (url.length > LIMITS.url) return null;
  return /^https?:\/\/[^\s<>"']+$/.test(url) ? url : null;
}

function sanitizePerson(person: EventPerson): EventPerson | null {
  const email = sanitizeEmail(person.email);
  if (email === null) return null;
  const name = person.name === undefined ? undefined : sanitizeLine(person.name, LIMITS.personName);
  return name === undefined || name === '' ? { email } : { name, email };
}

function sanitizeRecurrence(recurrence: EventRecurrence): EventRecurrence {
  const { count, interval } = recurrence;
  return {
    ...recurrence,
    ...(interval === undefined
      ? {}
      : { interval: Math.min(Math.max(Math.trunc(interval), 1), LIMITS.recurrenceInterval) }),
    ...(count === undefined
      ? {}
      : { count: Math.min(Math.max(Math.trunc(count), 1), LIMITS.recurrenceCount) }),
  };
}

/**
 * A draft safe to render and emit. Drops what can't be made safe (an unusable email, a
 * `javascript:` URL) rather than passing it through in a weakened form.
 */
export function sanitizeDraft(draft: EventDraft): EventDraft {
  const url = draft.url === undefined ? undefined : sanitizeUrl(draft.url);
  const attendees = (draft.attendees ?? [])
    .slice(0, LIMITS.attendees)
    .map(sanitizePerson)
    .filter((person): person is EventPerson => person !== null);
  const organizer = draft.organizer === undefined ? null : sanitizePerson(draft.organizer);
  const reminders = (draft.remindersMinutesBefore ?? [])
    .filter((minutes) => Number.isFinite(minutes) && minutes >= 0)
    .map((minutes) => Math.trunc(minutes))
    .slice(0, LIMITS.reminders);

  // Built field by field, never by spreading the input: a spread would carry a rejected value
  // (a `javascript:` URL, an unusable organizer) straight past the checks above.
  return {
    title: sanitizeLine(draft.title, LIMITS.title),
    ...(draft.status === undefined ? {} : { status: draft.status }),
    // Two known values or nothing: an unrecognised one would reach TRANSP verbatim.
    ...(draft.showAs === 'busy' || draft.showAs === 'free' ? { showAs: draft.showAs } : {}),
    start: sanitizeTime(draft.start),
    ...(draft.end === undefined ? {} : { end: sanitizeTime(draft.end) }),
    ...(draft.description === undefined
      ? {}
      : { description: sanitizeText(draft.description, LIMITS.description) }),
    ...(draft.location === undefined
      ? {}
      : { location: sanitizeLine(draft.location, LIMITS.location) }),
    ...(url === null || url === undefined ? {} : { url }),
    ...(organizer === null ? {} : { organizer }),
    ...(attendees.length === 0 ? {} : { attendees }),
    ...(reminders.length === 0 ? {} : { remindersMinutesBefore: reminders }),
    ...(draft.recurrence === undefined ? {} : { recurrence: sanitizeRecurrence(draft.recurrence) }),
    ...(draft.uid === undefined ? {} : { uid: sanitizeLine(draft.uid, 200) }),
  };
}

/** Zone names ride in an iCalendar parameter, where `:`, `;`, and `"` would end the value. */
function sanitizeTime<T extends EventDraft['start']>(time: T): T {
  if (time.kind === 'date' || time.timeZone === undefined) return time;
  const timeZone = sanitizeLine(time.timeZone, 100).replace(/[";:,]/g, '');
  return (
    timeZone === '' ? { kind: 'dateTime', dateTime: time.dateTime } : { ...time, timeZone }
  ) as T;
}
